/**
 * The tenant edit form's state, and the diff that turns it back into a PATCH.
 *
 * THE SCREEN SENDS WHAT CHANGED, NOT WHAT IT IS HOLDING. The obvious
 * implementation posts every field the form knows about and lets the API work
 * out which of them differ. It would be correct — `updateTenantResponse.changes`
 * is computed against the stored row, so the audit trail would come out the same
 * either way — and it would still be the wrong shape, for two reasons that only
 * show up in use:
 *
 *   1. **The screen has to state what it is about to do before it does it.**
 *      "Save 3 changes" and "Save" are different buttons, and only the first can
 *      be written by a form that knows its own diff. A confirmation that cannot
 *      name what it is confirming is a confirmation nobody reads.
 *   2. **An empty diff must be answerable without a round trip.**
 *      `updateTenantRequestSchema` refuses an empty `changes` outright, so a
 *      form that re-posts everything can never produce that refusal, and a form
 *      that posts nothing would get back a validation error phrased for an API
 *      client. Catching it here lets the screen say "nothing has changed" —
 *      which is the truth, and is not a failure.
 *
 * ABSENT, `null` AND A VALUE ARE THREE DIFFERENT THINGS, and {@link buildChanges}
 * is where the third one is produced. A blank optional field that was *already*
 * blank contributes nothing to the body; a blank optional field that used to
 * hold something becomes an explicit `null`, which is how a website entered by
 * mistake gets cleared. Collapsing those two — the natural result of building
 * the body from the form state alone — would either make clearing a field
 * impossible or make every save clear every empty field. The contract admits
 * `null` only on the columns that are genuinely nullable in `tenants`, so a
 * blank *name* stays a blank name and fails validation as a name, naming the
 * field, rather than failing later as a NOT NULL violation naming a constraint.
 *
 * THE EXPIRY IS COMPARED AS A CALENDAR DAY, NOT AS AN INSTANT — see
 * `lib/date-input.ts`. Comparing the ISO strings would report the expiry as
 * changed on every save of any tenant whose stored `valid_till` is not exactly
 * 23:59:59.999 in *this browser's* timezone, which is most of them, and would
 * write a permanent audit row saying a date moved when nobody touched it.
 *
 * FEATURES ARE COMPARED AS A SET, through `renderFeatureSet` — the same function
 * the audit trail renders them with. Comparing the arrays element-wise would
 * make a re-ordered checkbox list look like a change; comparing them with a
 * hand-rolled sort here would eventually disagree with the string the audit row
 * is written from, and the disagreement would read as a change that did not
 * happen.
 */
import {
  renderFeatureSet,
  type Feature,
  type Tenant,
  type TenantEditableFields,
  type TenantOrganisationEdit,
} from '@wend/shared';

import { isSameCalendarDay, toDateInputValue, toIsoEndOfDay } from '../../lib/date-input';

/* -------------------------------------------------------------------------- */
/* Form state                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Every editable field, as the controlled value its input holds.
 *
 * Strings rather than `string | undefined` throughout: a text input's empty
 * state is `''`, and modelling absence as `undefined` here would mean every
 * `onChange` had to decide whether the user had cleared a field or never
 * touched it. That decision belongs in {@link buildChanges}, once, where the
 * original value is in scope to compare against.
 */
export interface TenantEditFormState {
  name: string;
  email: string;
  phone: string;
  legalName: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId: string;
  /** `yyyy-mm-dd`, local, from a date input. Becomes the end of that day. */
  validTill: string;
  isActive: boolean;
  features: Feature[];
}

/** The organisation half, so the diff can be built one key at a time. */
type OrganisationFormKey = Extract<keyof TenantOrganisationEdit, string>;

/**
 * The form fields that hold a plain string.
 *
 * Narrowed from {@link TenantEditFormState} rather than listed, so `isActive`
 * and `features` cannot be reached by the text diff below even by a typo — a
 * boolean run through a trim-and-compare would silently stringify to `"true"`.
 */
type StringFormKey = {
  [K in keyof TenantEditFormState]: TenantEditFormState[K] extends string ? K : never;
}[keyof TenantEditFormState];

/**
 * Which form field feeds which organisation column.
 *
 * A `Record<OrganisationFormKey, …>` rather than a list of pairs, so a column
 * added to `tenantOrganisationEditSchema` fails this app's typecheck rather
 * than being quietly left out of the diff — a field the form renders, the user
 * edits, and the PATCH never mentions.
 */
const ORGANISATION_FIELDS: Readonly<Record<OrganisationFormKey, StringFormKey>> = {
  legalName: 'legalName',
  website: 'website',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  state: 'state',
  postalCode: 'postalCode',
  country: 'country',
  taxId: 'taxId',
};

const ORGANISATION_KEYS = Object.keys(ORGANISATION_FIELDS) as OrganisationFormKey[];

/** A tenant read shape as the form that edits it. The round trip is exact. */
export function formStateFromTenant(tenant: Tenant): TenantEditFormState {
  const organisation = tenant.organisation;

  return {
    name: tenant.name,
    email: tenant.email,
    phone: tenant.phone,
    legalName: organisation.legalName ?? '',
    website: organisation.website ?? '',
    addressLine1: organisation.addressLine1 ?? '',
    addressLine2: organisation.addressLine2 ?? '',
    city: organisation.city ?? '',
    state: organisation.state ?? '',
    postalCode: organisation.postalCode ?? '',
    country: organisation.country ?? '',
    taxId: organisation.taxId ?? '',
    validTill: toDateInputValue(tenant.validTill),
    isActive: tenant.isActive,
    // Copied, not aliased: the checkbox handler rebuilds this array, and a
    // shared reference would mutate the baseline the diff is measured against.
    features: [...tenant.features],
  };
}

/* -------------------------------------------------------------------------- */
/* The diff                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What a text field became, or `undefined` when it did not move.
 *
 * `null` is only ever produced for a field that held something and is now
 * blank. `nullable` is passed by the caller and is not inferred from the value:
 * a blank `name` must reach the contract as `''` and be refused as a name, not
 * as a `null` the contract has no member for.
 */
function textChange(
  before: string,
  after: string,
  nullable: boolean,
): string | null | undefined {
  const trimmed = after.trim();
  if (trimmed === before.trim()) return undefined;
  if (trimmed === '' && nullable) return null;
  return trimmed;
}

/**
 * The organisation half of the body, or `undefined` when nothing inside moved.
 *
 * Returning `undefined` rather than `{}` matters: `tenantEditFieldPaths` skips
 * an empty organisation object, so an empty one would not make the PATCH
 * invalid — it would just be a body that names an object and asks for no change
 * inside it, which is noise on the wire and a `changes` count that disagrees
 * with what the screen said it would do.
 */
function buildOrganisationChanges(
  initial: TenantEditFormState,
  current: TenantEditFormState,
): TenantOrganisationEdit | undefined {
  const organisation: Partial<Record<OrganisationFormKey, string | null>> = {};

  for (const key of ORGANISATION_KEYS) {
    const formKey = ORGANISATION_FIELDS[key];
    // Every organisation column is nullable in `tenants`, so every one of them
    // can be cleared — that is the whole reason `clearable()` exists.
    const change = textChange(initial[formKey], current[formKey], true);
    if (change !== undefined) organisation[key] = change;
  }

  return Object.keys(organisation).length === 0 ? undefined : organisation;
}

/**
 * Everything that differs between the tenant as loaded and the form as it
 * stands. An empty object means the operator changed nothing.
 *
 * `tenant` rather than the initial form state is the second argument for
 * `validTill`, because the comparison there is between a stored *instant* and a
 * chosen *day*, and the instant is the thing that must not be re-derived.
 */
export function buildChanges(
  tenant: Tenant,
  initial: TenantEditFormState,
  current: TenantEditFormState,
): TenantEditableFields {
  const changes: TenantEditableFields = {};

  // NOT NULL columns: `false` for nullable, so blanking one is refused as a bad
  // name / email / phone rather than accepted as a clear.
  const name = textChange(initial.name, current.name, false);
  if (name !== undefined && name !== null) changes.name = name;

  const email = textChange(initial.email, current.email, false);
  if (email !== undefined && email !== null) changes.email = email;

  const phone = textChange(initial.phone, current.phone, false);
  if (phone !== undefined && phone !== null) changes.phone = phone;

  const organisation = buildOrganisationChanges(initial, current);
  if (organisation !== undefined) changes.organisation = organisation;

  // A blank date input is left alone rather than sent: `validTill` is NOT NULL,
  // and an open-ended subscription is a far-off date, not an absent one.
  if (current.validTill !== '' && !isSameCalendarDay(tenant.validTill, current.validTill)) {
    const iso = toIsoEndOfDay(current.validTill);
    if (iso !== undefined) changes.validTill = iso;
  }

  if (current.isActive !== initial.isActive) changes.isActive = current.isActive;

  // Set comparison, through the audit trail's own renderer. See the header.
  if (renderFeatureSet(current.features) !== renderFeatureSet(initial.features)) {
    changes.features = current.features;
  }

  return changes;
}

/* -------------------------------------------------------------------------- */
/* Field errors                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Where a validation failure for an editable field lands.
 *
 * The request body is `{ changes, reason }`, so zod's path for a bad workspace
 * name is `changes.name`, not `name` — and the API's own `fieldErrors` use the
 * same body paths, which is what lets a server-side refusal highlight the same
 * input a client-side one does. Written as a function rather than typed out at
 * fifteen call sites so the prefix cannot be right in fourteen of them.
 */
export function editErrorKey(path: string): string {
  return `changes.${path}`;
}
